/**
 * Modal Context - Centralized modal management system
 * 
 * Provides modal functionality across the entire app including:
 * - Generic modals for any content
 * - Error/warning/info message modals
 * - Navigation confirmation modals
 * - Programmatic modal control
 */

import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'generic', // 'generic', 'error', 'warning', 'info', 'confirm'
    title: '',
    content: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showConfirm: true,
    showCancel: false,
    onConfirm: null,
    onCancel: null,
    onClose: null,
    customComponent: null,
    size: 'medium', // 'small', 'medium', 'large'
    closable: true,
  });

  // Generic modal opener
  const openModal = useCallback((config) => {
    setModalState({
      isOpen: true,
      type: 'generic',
      title: '',
      content: '',
      confirmText: 'OK',
      cancelText: 'Cancel',
      showConfirm: true,
      showCancel: false,
      onConfirm: null,
      onCancel: null,
      onClose: null,
      customComponent: null,
      size: 'medium',
      closable: true,
      ...config,
    });
  }, []);

  // Specific modal types
  const showError = useCallback((title, message, onClose = null) => {
    openModal({
      type: 'error',
      title: title || 'Error',
      content: message,
      confirmText: 'OK',
      showConfirm: true,
      showCancel: false,
      onConfirm: onClose,
      onClose,
    });
  }, [openModal]);

  const showWarning = useCallback((title, message, onClose = null) => {
    openModal({
      type: 'warning',
      title: title || 'Warning',
      content: message,
      confirmText: 'OK',
      showConfirm: true,
      showCancel: false,
      onConfirm: onClose,
      onClose,
    });
  }, [openModal]);

  const showInfo = useCallback((title, message, onClose = null) => {
    openModal({
      type: 'info',
      title: title || 'Information',
      content: message,
      confirmText: 'OK',
      showConfirm: true,
      showCancel: false,
      onConfirm: onClose,
      onClose,
    });
  }, [openModal]);

  const showConfirm = useCallback((title, message, onConfirm, onCancel = null) => {
    openModal({
      type: 'confirm',
      title: title || 'Confirm',
      content: message,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      showConfirm: true,
      showCancel: true,
      onConfirm,
      onCancel,
      onClose: onCancel,
    });
  }, [openModal]);

  // Show custom component modal
  const showComponent = useCallback((component, config = {}) => {
    openModal({
      type: 'component',
      customComponent: component,
      title: config.title || '',
      size: config.size || 'medium',
      closable: config.closable !== undefined ? config.closable : true,
      showConfirm: false,
      showCancel: false,
      onClose: config.onClose,
      ...config,
    });
  }, [openModal]);

  // Navigation confirmation modal
  const showNavigationConfirm = useCallback((options = {}) => {
    const {
      title = 'Unsaved Changes',
      message = 'You have unsaved changes. Are you sure you want to leave?',
      confirmText = 'Discard Changes',
      cancelText = 'Stay Here',
      onConfirm,
      onCancel,
    } = options;

    openModal({
      type: 'warning',
      title,
      content: message,
      confirmText,
      cancelText,
      showConfirm: true,
      showCancel: true,
      onConfirm,
      onCancel,
      onClose: onCancel,
    });
  }, [openModal]);

  // Close modal
  const closeModal = useCallback(() => {
    const { onClose } = modalState;
    setModalState(prev => ({ ...prev, isOpen: false }));
    
    // Call onClose callback if provided
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  }, [modalState]);

  // Handle confirm action
  const handleConfirm = useCallback(() => {
    const { onConfirm } = modalState;
    
    if (onConfirm && typeof onConfirm === 'function') {
      const result = onConfirm();
      
      // If onConfirm returns a promise, wait for it
      if (result instanceof Promise) {
        result.finally(() => {
          closeModal();
        });
      } else {
        closeModal();
      }
    } else {
      closeModal();
    }
  }, [modalState, closeModal]);

  // Handle cancel action
  const handleCancel = useCallback(() => {
    const { onCancel } = modalState;
    
    if (onCancel && typeof onCancel === 'function') {
      onCancel();
    }
    
    closeModal();
  }, [modalState, closeModal]);

  const value = {
    // State
    modalState,
    
    // Generic modal control
    openModal,
    closeModal,
    
    // Specific modal types
    showError,
    showWarning,
    showInfo,
    showConfirm,
    showComponent,
    showNavigationConfirm,
    
    // Action handlers
    handleConfirm,
    handleCancel,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export default ModalContext;
